import { useEffect } from "react";

/**
 * Keep the screen awake while a shopping list is open.
 *
 * The one screen in this app somebody holds without touching: a phone in one
 * hand, a basket in the other, reading the next three lines. Every twenty
 * seconds it goes dark and has to be woken with the hand that is holding the
 * basket.
 *
 * **Only while a list is actually open**, and released the moment it is not.
 * A lock held by a screen nobody is reading is a flat battery, and this is the
 * kind of promise that is easy to make and easy to forget to take back.
 *
 * The browser releases it by itself when the tab is hidden, and does not give
 * it back on return — hence the `visibilitychange` listener, which is the whole
 * reason this is more than two lines.
 */
export function useScreenAwake(wanted: boolean): void {
  useEffect(() => {
    if (!wanted) return;

    const lockable = navigator as Navigator & {
      wakeLock?: { request: (type: "screen") => Promise<WakeLockSentinel> };
    };
    if (!lockable.wakeLock) return;

    let sentinel: WakeLockSentinel | null = null;
    let dropped = false;

    const take = async () => {
      // Refused in a background tab, refused on low battery, absent on iOS
      // before 16.4. All of them mean "the screen will behave as it always
      // did", which is not a failure worth saying anything about.
      try {
        if (!dropped && document.visibilityState === "visible") {
          sentinel = (await lockable.wakeLock!.request("screen")) ?? null;
        }
      } catch {
        sentinel = null;
      }
    };

    const onVisible = () => void take();

    void take();
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      dropped = true;
      document.removeEventListener("visibilitychange", onVisible);
      void sentinel?.release().catch(() => undefined);
    };
  }, [wanted]);
}
